import { del, get, patch, post } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { omit } from "lodash";

import { AppType } from "@:constants";
import { BotCreateSchema, BotGetSchema, BotUpdateSchema } from "@:openapi";

/*
 * Because ai_settings should keep the format, so we don't transform request/response
 */
export interface Bots {
  GET_ONE: {
    RequestPath: { id: string };
    Response: BotGetSchema;
  };
  GET_ALL: {
    Response: Array<BotGetSchema>;
  };
  POST: {
    RequestBody: BotCreateSchema;
    Response: BotGetSchema;
  };
  PATCH: {
    RequestBody: BotUpdateSchema;
    RequestPath: { id: string };
    Response: BotGetSchema;
  };
  DELETE: {
    RequestPath: { id: string };
    Response: BotGetSchema;
  };
}

export const botQueryKey = createQueryKeys("bot", {
  getOne: (params: Bots["GET_ONE"]["RequestPath"]) => [params],
  getAll: (isEnabled: boolean) => [isEnabled],
});

export const useQueryGetOneBot = (
  params: Bots["GET_ONE"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<Bots["GET_ONE"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Bots["GET_ONE"]["Response"], AxiosError>({
    queryKey: botQueryKey.getOne(params).queryKey,
    queryFn: () => {
      return get<Bots["GET_ONE"]["Response"]>(
        `/api/bots/${params.id}`,
        undefined,
        {
          transformRequest: axios.defaults.transformRequest,
          transformResponse: axios.defaults.transformResponse,
        },
      );
    },
    ...options,
  });
};

const filterByEnabled = (
  data: Bots["GET_ALL"]["Response"],
  isEnabled: boolean,
): Bots["GET_ALL"]["Response"] => {
  if (!isEnabled) return data;
  return data.filter((bot) => {
    return bot?.enabled;
  });
};

export const filterByAppType = (
  data: Bots["GET_ALL"]["Response"],
  appType?: AppType,
): Bots["GET_ALL"]["Response"] => {
  if (!appType) return data;

  switch (appType) {
    case AppType.Audio:
      return data.filter((bot) => !!bot?.ai_settings?.audio);
    case AppType.Docs:
      return data.filter((bot) => !!bot?.ai_settings?.summarization);
    case AppType.Search:
      return data.filter((bot) => !!bot?.ai_settings?.search);
    case AppType.Translation:
      return data.filter((bot) => !!bot?.ai_settings?.document_translation);
    default:
      return [];
  }
};

export const useQueryGetAllBots = (
  isEnabled: boolean = false,
  appType?: AppType,
  options?: Omit<
    UseQueryOptions<Bots["GET_ALL"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Bots["GET_ALL"]["Response"], AxiosError>({
    queryKey: botQueryKey.getAll(isEnabled).queryKey,
    queryFn: () => {
      return get<Bots["GET_ALL"]["Response"]>("/api/bots", undefined, {
        transformRequest: axios.defaults.transformRequest,
        transformResponse: axios.defaults.transformResponse,
      });
    },
    select: (data) => {
      const bots = Array.isArray(data) ? data : [];
      return filterByAppType(filterByEnabled(bots, isEnabled), appType);
    },
    ...options,
  });
};

export const useMutationCreateBot = (
  options?: UseMutationOptions<
    Bots["POST"]["Response"],
    AxiosError,
    Bots["POST"]["RequestBody"]
  >,
) => {
  return useMutation({
    mutationFn: (data: Bots["POST"]["RequestBody"]) => {
      return post<Bots["POST"]["Response"]>("/api/bots", data, {
        transformRequest: axios.defaults.transformRequest,
        transformResponse: axios.defaults.transformResponse,
      });
    },
    ...options,
  });
};

export const useMutationUpdateBot = (
  options?: UseMutationOptions<
    Bots["PATCH"]["Response"],
    AxiosError,
    Bots["PATCH"]["RequestBody"] & Bots["PATCH"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (
      data: Bots["PATCH"]["RequestBody"] & Bots["PATCH"]["RequestPath"],
    ) => {
      return patch<Bots["PATCH"]["Response"]>(
        `/api/bots/${data.id}`,
        omit(data, ["id"]),
        {
          transformRequest: axios.defaults.transformRequest,
          transformResponse: axios.defaults.transformResponse,
        },
      );
    },
    ...options,
  });
};

export const useMutationDeleteBot = (
  options?: UseMutationOptions<
    Bots["DELETE"]["Response"],
    AxiosError,
    Bots["DELETE"]["RequestPath"]
  >,
) => {
  return useMutation({
    mutationFn: (params: Bots["DELETE"]["RequestPath"]) => {
      return del<Bots["DELETE"]["Response"]>(
        `/api/bots/${params.id}`,
        undefined,
        {
          transformRequest: axios.defaults.transformRequest,
          transformResponse: axios.defaults.transformResponse,
        },
      );
    },
    ...options,
  });
};
